import {
  LOGIN,
  LOGOUT,
  AUTH_ASYNC_START,
  AUTH_LOG_ERROR,
  LOGIN_PAGE_UNLOADED,
  SET_RESET_DETAILS,
  AUTH_TEAM_MEMBER_PASSWORD,
  RESET_PASSWORD,
} from '../types';
import Auth from 'services/auth';
import { showToast } from './toast';
import { setToken } from 'libs';
import FirebaseAnalytics, { AnalyticsLogStatus } from 'libs/analytics';

const login = (data) => ({
  type: LOGIN,
  ...data,
});

export const setLoading = (loading) => ({
  type: AUTH_ASYNC_START,
  loading,
});

const onLoginPageUnload = () => ({
  type: LOGIN_PAGE_UNLOADED,
});

export const logout = () => (dispatch) =>
  dispatch({
    type: LOGOUT,
  });

export const loginRequest = (body, onSuccess) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const {
      data: { jwt, data },
    } = await Auth.login(body);

    setToken(jwt);

    dispatch(
      login({
        accessData: { token: jwt, time: new Date() },
        user: { ...data, role: jwt.role },
        isAuthenticated: true,
      }),
    );

    FirebaseAnalytics.logWithValue('signin_as_a_user', {
      STATUS: AnalyticsLogStatus.SUCCESS,
    });

    onSuccess();
    return dispatch(onLoginPageUnload());
  } catch (error) {
    FirebaseAnalytics.logWithValue('signin_as_a_user', {
      STATUS: AnalyticsLogStatus.FAILED,
    });
    dispatch({
      type: AUTH_LOG_ERROR,
      error: error?.response?.data?.message || 'Something went wrong',
    });
    if (error?.response?.status === 412) {
      dispatch(
        showToast(
          'error',
          'Email not verified.',
          'Click link to resend code',
          'Resend Email',
          () => (window.location.href = '/auth/resend-email'),
        ),
      );
    } else {
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
    }
  }
};

export const forgotPasswordRequest =
  (email, onSuccess = () => null, onReset = () => null) =>
  async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const { data } = await Auth.forgotPassword(email);

      dispatch({
        type: SET_RESET_DETAILS,
        reset: { ...data, email },
      });
      dispatch(showToast('success', 'OTP sent to Email'));
      dispatch({
        type: RESET_PASSWORD,
        payload: { ...data, email },
      });
      return onSuccess();
    } catch (error) {
      dispatch({
        type: AUTH_LOG_ERROR,
        error: error?.response?.data?.message || 'Something went wrong',
      });
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
      return onReset();
    }
  };

export const verifyTokenRequest =
  (data, onSuccess = () => null, onReset = () => null) =>
  async (dispatch) => {
    dispatch(setLoading(true));
    try {
      await Auth.verifyToken(data);
      dispatch(setLoading(false));
      return onSuccess();
    } catch (error) {
      dispatch({
        type: AUTH_LOG_ERROR,
        error: error?.response?.data?.message || 'Something went wrong',
      });
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
    } finally {
      return onReset();
    }
  };

export const changePasswordRequest =
  (data, onSuccess = () => null, onFail = () => null) =>
  async (dispatch) => {
    try {
      await Auth.changePassword(data);
      dispatch(setLoading(false));
      dispatch(
        showToast(
          'success',
          'Password changed successfully, login with new password',
        ),
      );
      return onSuccess();
    } catch (error) {
      dispatch({
        type: AUTH_LOG_ERROR,
        error: error?.response?.data?.message || 'Something went wrong',
      });
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
      return onFail();
    }
  };

export const resetPasswordRequest =
  (data, onSuccess = () => null, onReset = () => null) =>
  async (dispatch) => {
    dispatch(setLoading(true));
    try {
      await Auth.resetPassword(data);
      dispatch(setLoading(false));
      dispatch(showToast('success', 'Password reset successfully'));
      return onSuccess();
    } catch (error) {
      dispatch({
        type: AUTH_LOG_ERROR,
        error: error?.response?.data?.message || 'Something went wrong',
      });
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
    } finally {
      return onReset();
    }
  };

export const createAccount = (body, onSuccess) => (dispatch) => {
  dispatch(setLoading(true));
  return Auth.createAccount(body)
    .then((data) => {
      dispatch(onLoginPageUnload());
      return onSuccess(data.data);
    })
    .catch(({ response }) => {
      if (response !== undefined && response.message) {
        dispatch({
          type: AUTH_LOG_ERROR,
          error: response?.data.message || 'An error occurred',
        });

        dispatch(
          showToast(
            'error',
            'Something went wrong',
            response?.data.message || 'An error occurred',
          ),
        );
      }
      dispatch({
        type: AUTH_LOG_ERROR,
      });
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          response?.data.message || 'An error occurred',
        ),
      );
    });
};

export const setTeamMember =
  (password, invitationId, onSuccess) => async (dispatch) => {
    dispatch({
      type: AUTH_TEAM_MEMBER_PASSWORD,
      payload: true,
    });

    try {
      await Auth.setPassword(password, invitationId);
      dispatch(showToast('success', 'Password set successfully'));
      dispatch({
        type: AUTH_TEAM_MEMBER_PASSWORD,
        payload: false,
      });
      return onSuccess();
    } catch (error) {
      dispatch({
        type: AUTH_TEAM_MEMBER_PASSWORD,
        payload: false,
      });
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
    }
  };

export const setPinRequest =
  (data, merchantId, onSuccess = () => null) =>
  async (dispatch) => {
    dispatch(setLoading(true));
    try {
      await Auth.setPin(data, merchantId);
      dispatch(setLoading(false));
      dispatch(showToast('success', 'Authentication pin set successfully'));
      return onSuccess();
    } catch (error) {
      dispatch({
        type: AUTH_LOG_ERROR,
        error: error?.response?.data?.message || 'Something went wrong',
      });
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
    }
  };

export const resendVerificationEmail =
  (emailAddress, onSuccess) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
      await Auth.resendVerificationEmail(emailAddress);
      dispatch(setLoading(false));
      return onSuccess();
    } catch (error) {
      dispatch(setLoading(false));
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
    }
  };

export const verifyEmailToken =
  (token, email, onSuccess, onError) => async (dispatch) => {
    try {
      await Auth.verifyEmailToken(token, email);
      return onSuccess();
    } catch (error) {
      dispatch(
        showToast(
          'error',
          'Something went wrong',
          error?.response?.data?.message || 'An error occurred',
        ),
      );
      return onError();
    }
  };
